import { createContext, useContext, useEffect, type FC } from 'react';

import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';

import SnackbarAlert from '~/components/SnackbarAlert/SnackbarAlert';
import { useSnackbarAlert } from '~/components/SnackbarAlert/hooks';
import {
  type AddSnackbarActionAlertType,
  type AddSnackbarAlertType,
} from '~/components/SnackbarAlert/types';
import { getSnackbarStore } from '~/selectors/snackbar.selectors';

type GlobalComponentsContext = {
  snackbar: {
    addSnackbarActionAlert: AddSnackbarActionAlertType;
    addSnackbarAlert: AddSnackbarAlertType;
  };
};

const GlobalComponentsContext = createContext<GlobalComponentsContext>({
  snackbar: {
    addSnackbarActionAlert: () => {},
    addSnackbarAlert: () => {},
  },
});

/**
 * A global provider that makes globally available components that can be shared across multiple routes
 * by exposing their hooks and mutation functions
 * @param children
 * @returns a GlobalComponentProvider with the global components and {children} in it.
 */
export const GlobalComponentsProvider: FC = ({ children }) => {
  const { addSnackbarActionAlert, addSnackbarAlert, snackbarAlert } = useSnackbarAlert();

  const snackbarStore = useSelector(getSnackbarStore);
  useEffect(() => {
    if (!snackbarStore.message) return;

    addSnackbarAlert(
      snackbarStore.i18n ? (
        <Trans i18nKey={snackbarStore.message} values={snackbarStore.i18nValues} />
      ) : (
        snackbarStore.message
      ),
      snackbarStore.severity,
    );
  }, [addSnackbarAlert, snackbarStore]);

  const globalComponents = {
    snackbar: {
      addSnackbarActionAlert,
      addSnackbarAlert,
    },
  };

  return (
    <GlobalComponentsContext.Provider value={globalComponents}>
      {snackbarAlert ? <SnackbarAlert {...snackbarAlert} /> : null}
      {children}
    </GlobalComponentsContext.Provider>
  );
};

export const useGlobalComponentsContext = () => {
  const { snackbar } = useContext(GlobalComponentsContext);

  return { snackbar };
};
